import React, { useState, useEffect } from 'react';
import { Navigation, Search, Filter, Download, Info, Settings } from 'lucide-react';
import WarehouseMap from '../warehouse/WarehouseMap';
import Warehouse3D from '../warehouse/Warehouse3D';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy';

const WarehouseMapPage = () => {
  const [selectedLocation, setSelectedLocation] = useState('');
  const [locationDetails, setLocationDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [mapView, setMapView] = useState('2d'); // '2d' or '3d'
  const [racks, setRacks] = useState([]);
  const [occupancyData, setOccupancyData] = useState({});
  const [filters, setFilters] = useState({
    showOccupied: true,
    showAvailable: true,
    rackFilter: 'all'
  });

  // Fetch warehouse data on component mount
  useEffect(() => {
    fetchWarehouseData();
  }, []);

  // Fetch location details when a location is selected
  useEffect(() => {
    if (selectedLocation) {
      fetchLocationDetails(selectedLocation);
    }
  }, [selectedLocation]);

  const fetchWarehouseData = async () => {
    try {
      // Fetch racks structure
      const racksResponse = await axios.get(`${API_BASE_URL}/api/warehouse/structure/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const racksData = Array.isArray(racksResponse.data) ? racksResponse.data : 
                       racksResponse.data.results ? racksResponse.data.results : [];
      setRacks(racksData);

      // Fetch occupancy data
      const occupancyResponse = await axios.get(`${API_BASE_URL}/api/warehouse/structure/occupancy/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const occupancyDataArray = Array.isArray(occupancyResponse.data) ? occupancyResponse.data : 
                                occupancyResponse.data.results ? occupancyResponse.data.results : [];
      const occupancyMap = {};
      occupancyDataArray.forEach(item => {
        occupancyMap[item.location] = true;
      });
      setOccupancyData(occupancyMap);
    } catch (error) {
      console.error('Error fetching warehouse data:', error);
    }
  };

  const fetchLocationDetails = async (locationCode) => {
    try {
      console.log('Fetching details for location:', locationCode);
      
      // Get items at this location
      const response = await axios.get(`${API_BASE_URL}/api/warehouse/items/?location=${locationCode}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      console.log('Location details response:', response.data);
      
      const items = response.data.results || response.data || [];
      setLocationDetails({
        location: locationCode,
        items: items,
        totalValue: items.reduce(
          (sum, item) => sum + (item.quantity_in_stock * item.unit_price), 0
        )
      });
    } catch (error) {
      console.error('Error fetching location details:', error);
      console.error('Error response:', error.response?.data);
      
      // No need for alternative endpoint since /api/warehouse/items/ is the correct one
      setLocationDetails({
        location: locationCode,
        items: [],
        totalValue: 0
      });
    }
  };

  const exportMapData = () => {
    // TODO: Implement map data export functionality
    alert('Export functionality will be implemented');
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <Navigation className="mr-3 h-8 w-8 text-blue-600" />
                Warehouse Map
              </h1>
              <p className="text-gray-600 mt-2">
                Interactive map of warehouse layout with real-time occupancy data
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* View Toggle */}
              <div className="flex items-center bg-white border border-gray-300 rounded-lg overflow-hidden">
                <button
                  onClick={() => setMapView('2d')}
                  className={`px-4 py-2 text-sm transition-colors ${
                    mapView === '2d' 
                      ? 'bg-blue-600 text-white' 
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  2D Map
                </button>
                <button
                  onClick={() => setMapView('3d')}
                  className={`px-4 py-2 text-sm transition-colors ${
                    mapView === '3d' 
                      ? 'bg-blue-600 text-white' 
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  3D View
                </button>
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                  showFilters 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
              </button>
              
              <button
                onClick={exportMapData}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </button>
            </div>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mb-6 bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Map Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Location
                </label>
                <div className="relative">
                  <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="e.g., A-01-B, Rack A..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rack Filter
                </label>
                <select
                  value={filters.rackFilter}
                  onChange={(e) => setFilters({...filters, rackFilter: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Racks</option>
                  <option value="A">Rack A</option>
                  <option value="B">Rack B</option>
                  <option value="C">Rack C</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Show Locations
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.showAvailable}
                      onChange={(e) => setFilters({...filters, showAvailable: e.target.checked})}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Available</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.showOccupied}
                      onChange={(e) => setFilters({...filters, showOccupied: e.target.checked})}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Occupied</span>
                  </label>
                </div>
              </div>

              <div className="flex items-end">
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setFilters({
                      showOccupied: true,
                      showAvailable: true,
                      rackFilter: 'all'
                    });
                  }}
                  className="w-full px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Clear All
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main Content - Map and Details */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Map */}
          <div className="lg:col-span-3">
            {mapView === '2d' ? (
              <WarehouseMap 
                selectedLocation={selectedLocation}
                onLocationSelect={setSelectedLocation}
                showOccupancy={true}
                searchTerm={searchTerm}
                filters={filters}
              />
            ) : (
              <Warehouse3D 
                racks={racks}
                occupancyData={occupancyData}
                selectedLocation={selectedLocation}
                onLocationSelect={setSelectedLocation}
              />
            )}
          </div>

          {/* Location Details Sidebar */}
          <div className="space-y-6">
            {/* Location Info */}
            {selectedLocation ? (
              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Info className="h-5 w-5 mr-2 text-blue-600" />
                  Location Details
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-mono text-xl font-bold text-gray-900">
                      {selectedLocation}
                    </h4>
                    {selectedLocation.includes('-') && (
                      <div className="text-sm text-gray-600 mt-1">
                        Rack: {selectedLocation.split('-')[0]} | 
                        Shelf: {selectedLocation.split('-')[1]} | 
                        Container: {selectedLocation.split('-')[2]}
                      </div>
                    )}
                  </div>

                  {locationDetails && (
                    <>
                      <div className="border-t pt-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium text-gray-700">Items Stored:</span>
                          <span className="text-sm font-bold text-gray-900">
                            {locationDetails.items.length}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-700">Total Value:</span>
                          <span className="text-sm font-bold text-green-600">
                            €{locationDetails.totalValue.toFixed(2)}
                          </span>
                        </div>
                      </div>

                      {locationDetails.items.length > 0 ? (
                        <div className="border-t pt-4">
                          <h5 className="text-sm font-medium text-gray-700 mb-2">Stored Items:</h5>
                          <div className="space-y-2 max-h-40 overflow-y-auto">
                            {locationDetails.items.map((item, index) => (
                              <div key={index} className="bg-gray-50 rounded p-2">
                                <div className="text-sm font-medium text-gray-900">
                                  {item.name || item.item_name || 'Unknown Item'}
                                </div>
                                <div className="text-xs text-gray-600">
                                  SKU: {item.sku || item.item_sku || 'N/A'}
                                </div>
                                <div className="text-xs text-gray-600">
                                  Qty: {item.quantity_in_stock || item.quantity || 0} {item.unit || 'pcs'} | 
                                  €{item.unit_price || 0}
                                </div>
                                {item.description && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    {item.description}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="border-t pt-4">
                          <p className="text-sm text-gray-500 italic">
                            No items stored at this location
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <button
                  onClick={() => setSelectedLocation('')}
                  className="w-full mt-4 px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Clear Selection
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Info className="h-5 w-5 mr-2 text-blue-600" />
                  Location Details
                </h3>
                <div className="text-center text-gray-500 py-8">
                  <Navigation className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm">Click a location on the map to see details</p>
                </div>
              </div>
            )}

            {/* Quick Stats */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Total Racks:</span>
                  <span className="text-sm font-semibold text-gray-900">{racks.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Total Locations:</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {racks.reduce((sum, rack) => sum + (rack.shelves * rack.containers), 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Occupied:</span>
                  <span className="text-sm font-semibold text-red-600">
                    {Object.keys(occupancyData).length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Available:</span>
                  <span className="text-sm font-semibold text-green-600">
                    {racks.reduce((sum, rack) => sum + (rack.shelves * rack.containers), 0) - Object.keys(occupancyData).length}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WarehouseMapPage;